import { useCallback, useEffect, useRef, useState } from 'react';

// TypeScript's DOM lib doesn't ship the SpeechRecognition interface itself, only the result types
interface SpeechRecognitionEvent extends Event {
  readonly resultIndex: number;
  readonly results: SpeechRecognitionResultList;
}

interface SpeechRecognitionErrorEvent extends Event {
  readonly error: string;
}

interface SpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEvent) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}

type SpeechRecognitionConstructor = new () => SpeechRecognition;

export type DictationError = 'permission-denied' | 'no-microphone' | 'network' | 'unknown';

interface Options {
  lang?: string;
  onFinalTranscript: (transcript: string) => void;
}

const getSpeechRecognition = (): SpeechRecognitionConstructor | undefined => {
  const speechWindow = window as Window & {
    SpeechRecognition?: SpeechRecognitionConstructor;
    webkitSpeechRecognition?: SpeechRecognitionConstructor;
  };

  return speechWindow.SpeechRecognition ?? speechWindow.webkitSpeechRecognition;
};

const toDictationError = (error: string): DictationError | null => {
  switch (error) {
    // Silence and our own abort() are not failures
    case 'no-speech':
    case 'aborted':
      return null;
    case 'not-allowed':
    case 'service-not-allowed':
      return 'permission-denied';
    case 'audio-capture':
      return 'no-microphone';
    case 'network':
      return 'network';
    default:
      return 'unknown';
  }
};

export function useDictation({ lang = 'en-US', onFinalTranscript }: Options) {
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const onFinalTranscriptRef = useRef(onFinalTranscript);
  onFinalTranscriptRef.current = onFinalTranscript;

  const [isListening, setIsListening] = useState(false);
  const [interimTranscript, setInterimTranscript] = useState('');
  const [error, setError] = useState<DictationError | null>(null);

  const isSupported = getSpeechRecognition() !== undefined;

  const start = useCallback(() => {
    const SpeechRecognition = getSpeechRecognition();
    if (!SpeechRecognition || recognitionRef.current) return;

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = lang;

    recognition.onresult = event => {
      let finalTranscript = '';
      let interim = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) finalTranscript += result[0].transcript;
        else interim += result[0].transcript;
      }

      setInterimTranscript(interim);

      if (finalTranscript.trim()) {
        onFinalTranscriptRef.current(finalTranscript.trim());
      }
    };

    recognition.onerror = event => {
      const dictationError = toDictationError(event.error);
      if (dictationError) setError(dictationError);
    };

    // Fires after stop()/abort(), after errors, and when the browser ends the session on its own
    recognition.onend = () => {
      recognitionRef.current = null;
      setIsListening(false);
      setInterimTranscript('');
    };

    setError(null);

    try {
      recognition.start();
      recognitionRef.current = recognition;
      setIsListening(true);
    } catch {
      setError('unknown');
    }
  }, [lang]);

  // stop() lets the browser finalize any pending speech, abort() discards it
  const stop = useCallback(() => recognitionRef.current?.stop(), []);

  useEffect(
    () => () => {
      const recognition = recognitionRef.current;
      if (!recognition) return;

      // Detach first so late events don't update state after unmount
      recognition.onresult = null;
      recognition.onerror = null;
      recognition.onend = null;
      recognition.abort();
    },
    []
  );

  return { isSupported, isListening, interimTranscript, error, start, stop };
}
